import React, { useState, useEffect } from 'react';
import { TextField, FormControl } from '@material-ui/core';
import Swal from 'sweetalert2';
import { connect } from 'react-redux';
import { sendVerificationEmail, checkCooldown } from '../crud/emailVerification.crud';
import * as authActions from '../store/ducks/auth.duck';

function EmailConfirmationModal({ isOpen, onClose, userEmail, onEmailSent, user, fulfillUser }) {
    const [isEditing, setIsEditing] = useState(false);
    const [email, setEmail] = useState(userEmail || '');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [confirmEmailError, setConfirmEmailError] = useState('');
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    useEffect(() => {
        if (isOpen) {
            const emailToUse = user?.email || userEmail || '';
            setEmail(emailToUse);
            setConfirmEmail('');
            setIsEditing(false);
            setEmailError('');
            setConfirmEmailError('');
            
            if (emailToUse) {
                checkCooldown(emailToUse)
                    .then(({ data }) => {
                        if (data?.has_cooldown && data?.cooldown_seconds > 0) {
                            setCooldownSeconds(data.cooldown_seconds);
                        } else {
                            setCooldownSeconds(0);
                        }
                    })
                    .catch(() => {
                        setCooldownSeconds(0);
                    });
            } else {
                setCooldownSeconds(0);
            }
        }
    }, [isOpen, userEmail, user]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .swal2-container {
                z-index: 10002 !important;
            }
            .swal2-popup {
                z-index: 10002 !important;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    useEffect(() => {
        let interval = null;
        if (cooldownSeconds > 0) {
            interval = setInterval(() => {
                setCooldownSeconds((prev) => {
                    const newValue = prev - 1;
                    return newValue <= 0 ? 0 : newValue;
                });
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [cooldownSeconds]);

    const validateEmail = (emailToValidate) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailToValidate);
    };

    const handleConfirmEmail = async () => {
        if (isLoading || cooldownSeconds > 0) {
            return;
        }

        const emailToUse = user?.email || userEmail;
        if (!emailToUse) {
            Swal.fire({
                title: 'Erro',
                text: 'Dados do usuário não encontrados.',
                icon: 'error',
            });
            return;
        }

        Swal.fire({
            title: 'Email sendo enviado...',
            text: 'O email de verificação chegará na sua caixa de entrada em instantes.',
            icon: 'info',
            timer: 2000,
            showConfirmButton: false,
        });

        setIsLoading(true);

        try {
            await sendVerificationEmail(emailToUse);
            setCooldownSeconds(3600);
            Swal.fire({
                title: 'Sucesso!',
                text: 'Email de verificação enviado. O email pode demorar alguns minutos para chegar. Verifique também sua pasta de SPAM.',
                icon: 'success',
                confirmButtonText: 'OK',
            });
            onEmailSent && onEmailSent();
        } catch (error) {
            const errorMessage = error.response?.data?.errors?.[0] || 
                              error.response?.data?.message || 
                              'Erro ao enviar email de verificação. Tente novamente.';
            
            if (error.response?.status === 429) {
                const match = errorMessage.match(/(\d+)\s+minuto\(s\)\s+e\s+(\d+)\s+segundo\(s\)/);
                if (match) {
                    const minutes = parseInt(match[1]);
                    const seconds = parseInt(match[2]);
                    setCooldownSeconds(minutes * 60 + seconds);
                }
            }
            
            Swal.fire({
                title: 'Erro',
                text: errorMessage,
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditEmail = () => {
        setIsEditing(true);
        const emailToUse = user?.email || userEmail || '';
        setEmail(emailToUse);
        setConfirmEmail('');
        setEmailError('');
        setConfirmEmailError('');
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        
        if (value && !validateEmail(value)) {
            setEmailError('Email inválido');
        } else {
            setEmailError('');
        }

        if (confirmEmail && value !== confirmEmail) {
            setConfirmEmailError('Os emails não coincidem');
        } else {
            setConfirmEmailError('');
        }
    };

    const handleConfirmEmailChange = (e) => {
        const value = e.target.value;
        setConfirmEmail(value);
        
        if (value && value !== email) {
            setConfirmEmailError('Os emails não coincidem');
        } else {
            setConfirmEmailError('');
        }
    };

    const handleSendNewEmail = async () => {
        if (isLoading || cooldownSeconds > 0) {
            return;
        }
        if (!email) {
            setEmailError('Email é obrigatório');
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Email inválido');
            return;
        }

        if (!confirmEmail) {
            setConfirmEmailError('Confirme o email');
            return;
        }

        if (email !== confirmEmail) {
            setConfirmEmailError('Os emails não coincidem');
            return;
        }

        Swal.fire({
            title: 'Email sendo enviado...',
            text: 'O email de verificação chegará na sua caixa de entrada em instantes.',
            icon: 'info',
            timer: 2000,
            showConfirmButton: false,
        });

        setIsLoading(true);

        try {
            await sendVerificationEmail(email);
            setCooldownSeconds(3600);
            
            const emailToCompare = user?.email || userEmail;
            const isNewEmail = email !== emailToCompare;
            
            Swal.fire({
                title: 'Sucesso!',
                text: isNewEmail 
                    ? 'Email de verificação enviado para o novo endereço. O email pode demorar alguns minutos para chegar. Verifique também sua pasta de SPAM.' 
                    : 'Email de verificação enviado. O email pode demorar alguns minutos para chegar. Verifique também sua pasta de SPAM.',
                icon: 'success',
                confirmButtonText: 'OK',
            });
            onEmailSent && onEmailSent();
        } catch (error) {
            const errorMessage = error.response?.data?.errors?.[0] || 
                              error.response?.data?.message || 
                              'Erro ao atualizar email ou enviar verificação. Tente novamente.';
            
            if (error.response?.status === 429) {
                const match = errorMessage.match(/(\d+)\s+minuto\(s\)\s+e\s+(\d+)\s+segundo\(s\)/);
                if (match) {
                    const minutes = parseInt(match[1]);
                    const seconds = parseInt(match[2]);
                    setCooldownSeconds(minutes * 60 + seconds);
                }
            }
            
            Swal.fire({
                title: 'Erro',
                text: errorMessage,
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setIsLoading(false);
        }
    };

    const isSendButtonDisabled = () => {
        if (!isEditing) return isLoading || cooldownSeconds > 0;
        return isLoading || cooldownSeconds > 0 || !email || !confirmEmail || email !== confirmEmail || !!emailError || !!confirmEmailError;
    };

    if (!isOpen) return null;

    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
    };

    const modalStyle = {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
    };

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    };

    const closeButtonStyle = {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#666'
    };

    const contentStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    };

    const questionStyle = {
        fontSize: '16px',
        fontWeight: 500,
        marginBottom: '8px'
    };

    const emailDisplayStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        marginBottom: '16px'
    };

    const buttonsStyle = {
        display: 'flex',
        gap: '12px',
        marginTop: '8px'
    };

    const buttonBaseStyle = {
        flex: 1,
        padding: '12px 24px',
        borderRadius: '4px',
        border: 'none',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.3s'
    };

    const confirmButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: '#284EEA',
        color: 'white'
    };

    const editButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: '#f5f5f5',
        color: '#333'
    };

    const cancelButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: '#f5f5f5',
        color: '#333'
    };

    const sendButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: '#284EEA',
        color: 'white'
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <h3 style={{ margin: 0 }}>Confirmar Email</h3>
                    <button style={closeButtonStyle} onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div style={contentStyle}>
                    {!isEditing ? (
                        <>
                            <p style={questionStyle}>Este é o seu email correto?</p>
                            <div style={emailDisplayStyle}>
                                <i className="fas fa-envelope" style={{ color: '#284EEA' }}></i>
                                <span>{user?.email || userEmail}</span>
                            </div>
                            <div style={buttonsStyle}>
                                <button 
                                    style={{
                                        ...confirmButtonStyle,
                                        opacity: (isLoading || cooldownSeconds > 0) ? 0.6 : 1,
                                        cursor: (isLoading || cooldownSeconds > 0) ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={handleConfirmEmail}
                                    disabled={isLoading || cooldownSeconds > 0}
                                >
                                    {isLoading ? (
                                        <i className='fa fa-spinner fa-spin' />
                                    ) : cooldownSeconds > 0 ? (
                                        `Aguarde ${Math.ceil(cooldownSeconds / 60)} minutos`
                                    ) : (
                                        'Sim, este é meu email'
                                    )}
                                </button>
                                <button 
                                    style={{
                                        ...editButtonStyle,
                                        opacity: (isLoading || cooldownSeconds > 0) ? 0.6 : 1,
                                        cursor: (isLoading || cooldownSeconds > 0) ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={handleEditEmail}
                                    disabled={isLoading || cooldownSeconds > 0}
                                >
                                    Não, quero alterar
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p style={questionStyle}>Digite seu novo email</p>
                            <FormControl fullWidth>
                                <TextField
                                    label="Email"
                                    type="email"
                                    variant="outlined"
                                    value={email}
                                    onChange={handleEmailChange}
                                    error={!!emailError}
                                    helperText={emailError}
                                    disabled={isLoading}
                                    autoFocus
                                    margin="normal"
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <TextField
                                    label="Confirmar Email"
                                    type="email"
                                    variant="outlined"
                                    value={confirmEmail}
                                    onChange={handleConfirmEmailChange}
                                    error={!!confirmEmailError}
                                    helperText={confirmEmailError}
                                    disabled={isLoading}
                                    margin="normal"
                                />
                            </FormControl>
                            <div style={buttonsStyle}>
                                <button 
                                    style={{
                                        ...cancelButtonStyle,
                                        opacity: isLoading ? 0.6 : 1,
                                        cursor: isLoading ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => setIsEditing(false)}
                                    disabled={isLoading}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    style={{
                                        ...sendButtonStyle,
                                        opacity: isSendButtonDisabled() ? 0.6 : 1,
                                        cursor: isSendButtonDisabled() ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={handleSendNewEmail}
                                    disabled={isSendButtonDisabled()}
                                >
                                    {isLoading ? (
                                        <i className='fa fa-spinner fa-spin' />
                                    ) : cooldownSeconds > 0 ? (
                                        `Aguarde ${Math.ceil(cooldownSeconds / 60)} minutos`
                                    ) : (
                                        'Enviar'
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = ({ auth: { user } }) => ({ user });

const mapDispatchToProps = {
    fulfillUser: authActions.actions.fulfillUser
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailConfirmationModal);
